import { useCallback, useEffect, useMemo } from 'react';
import { translateEventToInventoryAccordionItem } from 'src/components/Accordions';
import { ActivePosEntityProvider } from 'src/contexts/ActivePosEntityContext';
import { ActiveSortTableColumnProvider } from 'src/contexts/ActiveSortTableColumnContext';
import { useAppContext } from 'src/contexts/AppContext';
import { BulkEditHubContextProvider } from 'src/contexts/BulkEditHubContext';
import {
  CatalogDataContextProvider,
  ExpandedEventData,
} from 'src/contexts/CatalogDataContext';
import { CatalogMultiSelectionContextProvider } from 'src/contexts/CatalogMultiSelectionContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { DialogProvider } from 'src/contexts/DialogContext/DialogContext';
import {
  FilterQueryContextProvider,
  useFilterQueryContext,
} from 'src/contexts/FilterQueryContext';
import { ListingStatusContextProvider } from 'src/contexts/ListingStatusContext';
import {
  MultiSelectionContextProvider,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { getListingsSectionalDataForEvents } from 'src/navigations/Routes/Inventory';
import { NoData } from 'src/tables/Table';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { DefaultListingTableSorting } from 'src/utils/constants/tableColumns';
import {
  DefaultListingQuery,
  EmptyListingQuery,
} from 'src/utils/eventQueryUtils';
import { transformData } from 'src/utils/eventWithDataUtils';
import { ListingCatalogQueryKey } from 'src/utils/inventoryUtils';
import { SectionType } from 'src/utils/types/sectionType';
import {
  ActionOutboxEntityType,
  ListingClient,
  ListingDetailDataField,
  ListingDetails,
  ListingQuery,
  UserSetting,
} from 'src/WebApiController';

import * as styles from '../../Events/EventPage/EventPage.css';
import { useAccordionItem } from '../../Events/EventPage/hooks/useAccordionItem';
import { SidePanelToolbar } from '../../Events/EventPage/SidePanel/SidePanelToolbar';
import { ListingSideTableFilterBar } from '../ListingFilterBar/ListingSideTableFilterBar';

const Inventory = ({
  viagVirtId,
  queryInput,
}: {
  viagVirtId: string;
  queryInput?: Partial<Pick<ListingQuery, 'purchaseOrderIds' | 'saleIds'>>;
}) => {
  const { isLoading, isItemsLoading, accordionItemProps } =
    useAccordionItem(viagVirtId);

  const { setSelectionMode } = useMultiSelectionContext();
  useEffect(() => {
    setSelectionMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viagVirtId]);

  const { setFilterQuery, filterQuery } = useFilterQueryContext<ListingQuery>();
  useEffect(() => {
    setFilterQuery({
      ...filterQuery,
      ...queryInput,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryInput, setFilterQuery]);

  const inventoryAccordionItem = useMemo(
    () =>
      accordionItemProps &&
      translateEventToInventoryAccordionItem(accordionItemProps, 0, true),
    [accordionItemProps]
  );

  const { BodyComponent, AfterHeaderComponent } = inventoryAccordionItem || {};

  const listingIds = inventoryAccordionItem?.entities?.map((l) => l.id) || [];

  const noData =
    (!isLoading && !inventoryAccordionItem) ||
    (!isItemsLoading && !accordionItemProps?.entities?.listings);

  const hasSelectedEntities =
    (queryInput?.saleIds && queryInput?.saleIds?.length > 0) ||
    (queryInput?.purchaseOrderIds && queryInput?.purchaseOrderIds?.length > 0);

  return (
    <>
      <div className={styles.sidePanelToolbar}>
        <SidePanelToolbar
          sectionType={SectionType.InventorySideTable}
          filterBar={<ListingSideTableFilterBar />}
          noData={noData}
          entityIds={hasSelectedEntities ? listingIds : undefined}
        />
        {!noData && AfterHeaderComponent && <AfterHeaderComponent />}
      </div>
      {isLoading && <PosSpinner />}
      {noData ? (
        <NoData>
          <FormatContent
            id={FormatContentId.NoDataAvailable}
            params={<Content id={ContentId.Listings} />}
          />
        </NoData>
      ) : (
        BodyComponent &&
        accordionItemProps && (
          <BodyComponent
            event={accordionItemProps.event}
            entities={accordionItemProps.entities.listings ?? []}
            entitiesSummary={accordionItemProps.entities.listingsSummary}
            entityCount={accordionItemProps.counts.listCnt ?? 0}
            listingGroupCount={accordionItemProps.counts.listGrpCnt}
            ungroupedListingCount={accordionItemProps.counts.ungrListCnt}
            isSideTable={true}
            useTableNavKeys={true}
            disablePagination={true}
            withOuterPadding={false}
          />
        )
      )}
    </>
  );
};

export const InventorySideTable = ({
  viagVirtId,
  queryInput,
}: {
  viagVirtId: string;
  queryInput?: Partial<Pick<ListingQuery, 'purchaseOrderIds' | 'saleIds'>>;
}) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();

  const getCatalogDataExpandedCallback = useCallback(
    (ids: string[], filterQuery: ListingQuery) => {
      return getListingsSectionalDataForEvents(
        ids,
        filterQuery,
        [
          ListingDetailDataField.Basic,
          ListingDetailDataField.Pricing,
          ListingDetailDataField.AutoPricing,
          ListingDetailDataField.InternalNotes,
        ],
        {
          activeAccountWebClientConfig,
          onError: (error) => {
            trackError(
              'ListingClient.getListingsSectionalDataForEvents',
              error,
              {
                ...filterQuery,
                eventIds: ids,
              }
            );
          },
        }
      );
    },
    [activeAccountWebClientConfig, trackError]
  );

  const onCatalogDataExpandedCallback = useCallback(
    (
      ids: string[],
      filterQuery: ListingQuery,
      currentData?: ExpandedEventData
    ) =>
      getListingsSectionalDataForEvents(
        ids,
        filterQuery,
        [ListingDetailDataField.Purchase, ListingDetailDataField.Tags],
        {
          activeAccountWebClientConfig,
          onError: (error) => {
            trackError(
              'ListingClient.getListingsSectionalDataForEvents',
              error,
              {
                ...filterQuery,
                eventIds: ids,
              }
            );
          },
        },
        currentData
      ),
    [activeAccountWebClientConfig, trackError]
  );

  const getActivePosEntity = useCallback(
    async (
      posEntityId: number,
      curEntity?: ListingDetails | null,
      dataToGet?: string[] | null
    ) => {
      if (activeAccountWebClientConfig.activeAccountId) {
        const listingDetail = await new ListingClient(
          activeAccountWebClientConfig
        ).getListingByListingId(
          posEntityId,
          curEntity ?? undefined,
          dataToGet?.map((s) => s as ListingDetailDataField) ?? []
        );

        if (listingDetail) {
          return {
            posEntityId: listingDetail.id,
            posEntity: listingDetail,
            posEntityDisplayId: listingDetail.idOnMkp,
          };
        }
      }
      return {};
    },
    [activeAccountWebClientConfig]
  );

  return (
    <BulkEditHubContextProvider>
      <FilterQueryContextProvider<ListingQuery>
        initialQuery={{
          ...DefaultListingQuery,
          eventOrMappingIds: [viagVirtId],
        }}
        emptyQuery={EmptyListingQuery}
        viewModeSettingId={UserSetting.InventoryPageViewMode}
        loadQueryFromUrl={false}
      >
        <CatalogDataContextProvider<ListingQuery>
          entityType={ActionOutboxEntityType.Listing}
          queryKey={ListingCatalogQueryKey}
          doNotLogPageLoad
          getCatalogDataExpanded={getCatalogDataExpandedCallback}
          onCatalogDataExpanded={onCatalogDataExpandedCallback}
          transformEventData={transformData}
        >
          <CatalogMultiSelectionContextProvider type={'listing'}>
            <MultiSelectionContextProvider>
              <ActivePosEntityProvider<ListingDetails>
                doNotLogPageLoad
                entityType={ActionOutboxEntityType.Listing}
                getActivePosEntity={getActivePosEntity}
              >
                <ListingStatusContextProvider>
                  <ActiveSortTableColumnProvider
                    entityType={ActionOutboxEntityType.Listing}
                    defaultSorting={DefaultListingTableSorting}
                  >
                    <DialogProvider>
                      <Inventory
                        viagVirtId={viagVirtId}
                        queryInput={queryInput}
                      />
                    </DialogProvider>
                  </ActiveSortTableColumnProvider>
                </ListingStatusContextProvider>
              </ActivePosEntityProvider>
            </MultiSelectionContextProvider>
          </CatalogMultiSelectionContextProvider>
        </CatalogDataContextProvider>
      </FilterQueryContextProvider>
    </BulkEditHubContextProvider>
  );
};
